import { useState, useCallback, useRef } from 'react';
import { verifyStepUp } from '../services/stepUpAuthService';

/**
 * Hook for step-up authentication flow.
 * 
 * Usage:
 *   const { requireStepUp, StepUpModal } = useStepUpAuth();
 *   
 *   const handleSensitiveAction = async () => {
 *     const token = await requireStepUp();
 *     if (!token) return; // user cancelled or failed
 *     await doSensitiveAction(token);
 *   };
 */
export function useStepUpAuth() {
  const [isOpen, setIsOpen] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const resolveRef = useRef(null);

  const requireStepUp = useCallback(() => {
    return new Promise((resolve) => {
      resolveRef.current = resolve;
      setError('');
      setIsOpen(true);
    });
  }, []);

  const handleVerify = useCallback(async (password) => {
    setLoading(true);
    setError('');
    try {
      const result = await verifyStepUp(password);
      setIsOpen(false);
      resolveRef.current?.(result.stepUpToken);
    } catch (err) {
      setError(err.message || 'Verification failed');
    } finally {
      setLoading(false);
    }
  }, []);

  const handleCancel = useCallback(() => {
    setIsOpen(false);
    setError('');
    resolveRef.current?.(null);
  }, []);

  return {
    requireStepUp,
    stepUpModalProps: {
      isOpen,
      error,
      loading,
      onVerify: handleVerify,
      onCancel: handleCancel,
    },
  };
}
